import * as THREE from 'three';
import { buildTickFieldData } from './geodesic';

const VERT = /* glsl */ `
attribute vec3 aColor;
attribute float aRate;
attribute float aPhase;
attribute float aSize;
uniform float uPx;
uniform float uTime;
varying vec3 vColor;
varying float vPulse;
void main() {
  vec4 mv = modelViewMatrix * vec4(position, 1.0);
  float ph = uTime * aRate * 4.0 + aPhase;
  float p = pow(0.5 + 0.5 * sin(ph), 4.0);
  vPulse = 0.35 + 0.65 * p;
  vColor = aColor;
  gl_PointSize = clamp(uPx * aSize * (0.7 + 0.6 * p) / max(-mv.z, 0.1), 1.0, 48.0);
  gl_Position = projectionMatrix * mv;
}`;

const FRAG = /* glsl */ `
uniform float uGain;
varying vec3 vColor;
varying float vPulse;
void main() {
  float d = length(gl_PointCoord - 0.5) * 2.0;
  float a = smoothstep(1.0, 0.0, d);
  a *= a;
  gl_FragColor = vec4(vColor * uGain, a * vPulse);
}`;

/**
 * The tick field: a lattice of markers that each pulse at the local clock rate. Quick and pale far from the Sun,
 * gold, then red and slow close to it (the ramp starts at blue, which only the far field's edge approaches).
 * The pulse rate is the clock rate to the 8th power, an exaggeration so the gradient shows.
 */
export class TickField {
  readonly points: THREE.Points;
  private material: THREE.ShaderMaterial;

  constructor() {
    const d = buildTickFieldData();
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(d.position, 3));
    g.setAttribute('aColor', new THREE.BufferAttribute(d.color, 3));
    g.setAttribute('aRate', new THREE.BufferAttribute(d.rate, 1));
    g.setAttribute('aPhase', new THREE.BufferAttribute(d.phase, 1));
    g.setAttribute('aSize', new THREE.BufferAttribute(d.size, 1));
    this.material = new THREE.ShaderMaterial({
      uniforms: { uPx: { value: 800 }, uTime: { value: 0 }, uGain: { value: 0.8 } },
      vertexShader: VERT,
      fragmentShader: FRAG,
      transparent: true,
      depthWrite: false,
      blending: THREE.AdditiveBlending
    });
    this.points = new THREE.Points(g, this.material);
  }

  /** `pxPerUnit` is the render height over twice the tangent of half the field of view: pixels per world unit at depth 1. */
  update(time: number, pxPerUnit: number) {
    this.material.uniforms.uTime.value = time;
    this.material.uniforms.uPx.value = pxPerUnit;
  }
}
